import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { getDetalleOto } from '../api/apiConfig';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { FaFilter, FaSort, FaSortDown, FaSortUp } from 'react-icons/fa';
import { IoSearchOutline, IoClose } from 'react-icons/io5';
import { MdDataset, MdReceipt, MdAccountBalance } from 'react-icons/md';
import { DETALLE_OTO_COLUMNS } from '../constants/detalleOtoColumns';
import { formatMoneyCLP, formatNumberEsCL, formatPercent } from '../utils/numberFormat';

const DEFAULT_PAGE_SIZE = 200;
/** Mismo tope que clamp_int en la Lambda (panel-base). */
const LAMBDA_MAX_PAGE_SIZE = 100_000;

function csvEscape(v) {
  const s = v === null || v === undefined ? '' : String(v);
  if (/[;"\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv(filename, content) {
  const blob = new Blob(['\ufeff' + content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/** Etiqueta estable para filtrar / listar valores (alineado a búsqueda global). */
function cellFilterLabel(v) {
  if (v === null || v === undefined || v === '') return '(Vacío)';
  return String(v);
}

/**
 * Filas que cumplen filtros de columna activos, excepto opcionalmente una columna
 * (para poblar el desplegable con valores aún visibles si se quitara ese filtro).
 */
function applyColumnFilters(rows, columnFilters, skipColumnKey) {
  const entries = Object.entries(columnFilters).filter(
    ([k, allowed]) => k !== skipColumnKey && Array.isArray(allowed) && allowed.length > 0,
  );
  if (entries.length === 0) return rows;
  return rows.filter((r) =>
    entries.every(([key, allowed]) => allowed.includes(cellFilterLabel(r[key]))),
  );
}

const PanelDetalleOto = () => {
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [reportTotal, setReportTotal] = useState(null);
  const [totalPages, setTotalPages] = useState(null);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [debugCesionesLoading, setDebugCesionesLoading] = useState(false);
  /** Filtros por columna (estilo Excel): lista de etiquetas permitidas por `key`. */
  const [columnFilters, setColumnFilters] = useState({});
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterMenuSearch, setFilterMenuSearch] = useState('');
  const [filterMenuChecked, setFilterMenuChecked] = useState({});
  const [filterMenuPos, setFilterMenuPos] = useState(null);
  const filterMenuRef = useRef(null);

  const [showCargaCampana, setShowCargaCampana] = useState(false);
  const [campanaNombre, setCampanaNombre] = useState('');
  const [fecIniCam, setFecIniCam] = useState('');
  const [fecFinCam, setFecFinCam] = useState('');
  const [idCamStart, setIdCamStart] = useState('');

  const cellAlign = (format) => {
    if (['money', 'ratioPercent', 'decimal', 'integer'].includes(format)) return 'right';
    return 'left';
  };

  const renderCell = (col, r) => {
    const v = r[col.key];
    if (v === null || v === undefined || v === '') return '—';
    switch (col.format) {
      case 'money':
        return formatMoneyCLP(v);
      case 'ratioPercent': {
        return formatPercent(v, { maximumFractionDigits: 1 });
      }
      case 'decimal': {
        const n = Number(v);
        if (Number.isNaN(n)) return String(v);
        return formatNumberEsCL(n, { minimumFractionDigits: 0, maximumFractionDigits: 4 });
      }
      case 'integer': {
        return formatNumberEsCL(v, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      }
      default:
        return String(v);
    }
  };

  useEffect(() => {
    let isMounted = true;
    const run = async () => {
      if (!user) return;
      try {
        setLoading(true);
        const result = await getDetalleOto(user, { page, page_size: pageSize });
        if (!isMounted) return;
        setRows(result?.data ?? []);
        const p = result?.pagination;
        if (p && typeof p.total === 'number') {
          setReportTotal(p.total);
          setTotalPages(typeof p.total_pages === 'number' ? p.total_pages : Math.ceil(p.total / pageSize) || null);
        } else {
          setReportTotal(null);
          setTotalPages(null);
        }
      } catch (e) {
        if (!isMounted) return;
        setRows([]);
        setReportTotal(null);
        setTotalPages(null);
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    };

    run();
    return () => { isMounted = false; };
  }, [user, page, pageSize]);

  const cargarTodosLosRegistros = () => {
    setPage(1);
    const n =
      reportTotal != null
        ? Math.min(LAMBDA_MAX_PAGE_SIZE, Math.max(reportTotal, 10))
        : LAMBDA_MAX_PAGE_SIZE;
    setPageSize(n);
  };

  const volverPaginacion = () => {
    setPage(1);
    setPageSize(DEFAULT_PAGE_SIZE);
  };

  const modoCargaCompleta = pageSize > DEFAULT_PAGE_SIZE;

  const textFilteredRows = useMemo(() => {
    if (!searchTerm.trim()) return rows;
    const q = searchTerm.toLowerCase().trim();
    return rows.filter((r) =>
      DETALLE_OTO_COLUMNS.some(({ key }) => {
        const v = r[key];
        return v != null && String(v).toLowerCase().includes(q);
      }),
    );
  }, [rows, searchTerm]);

  const columnFilteredRows = useMemo(
    () => applyColumnFilters(textFilteredRows, columnFilters),
    [textFilteredRows, columnFilters],
  );

  const sortedRows = useMemo(() => {
    if (!sortColumn) return columnFilteredRows;
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...columnFilteredRows].sort((a, b) => {
      let av = a?.[sortColumn];
      let bv = b?.[sortColumn];
      if (av == null) av = '';
      if (bv == null) bv = '';
      const an = typeof av === 'number' ? av : parseFloat(av);
      const bn = typeof bv === 'number' ? bv : parseFloat(bv);
      const isNumeric = !isNaN(an) && !isNaN(bn) && isFinite(an) && isFinite(bn);
      const cmp = isNumeric
        ? (an - bn)
        : String(av).toLowerCase().localeCompare(String(bv).toLowerCase(), 'es', { numeric: true, sensitivity: 'base' });
      return dir * cmp;
    });
  }, [columnFilteredRows, sortColumn, sortDirection]);

  const uniquesForOpenColumn = useMemo(() => {
    if (!openFilterKey) return [];
    const base = applyColumnFilters(textFilteredRows, columnFilters, openFilterKey);
    const uniq = new Set();
    for (const r of base) {
      uniq.add(cellFilterLabel(r[openFilterKey]));
    }
    return Array.from(uniq).sort((a, b) =>
      a.localeCompare(b, 'es', { numeric: true, sensitivity: 'base' }),
    );
  }, [openFilterKey, textFilteredRows, columnFilters]);

  const visibleFilterOptions = useMemo(() => {
    const q = filterMenuSearch.trim().toLowerCase();
    if (!q) return uniquesForOpenColumn;
    return uniquesForOpenColumn.filter((u) => u.toLowerCase().includes(q));
  }, [uniquesForOpenColumn, filterMenuSearch]);

  const activeColumnFilterCount = useMemo(
    () => Object.values(columnFilters).filter((a) => Array.isArray(a) && a.length > 0).length,
    [columnFilters],
  );

  useEffect(() => {
    if (!openFilterKey) return;
    const onDoc = (e) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(e.target)) {
        setOpenFilterKey(null);
        setFilterMenuPos(null);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [openFilterKey]);

  const openColumnFilterMenu = (colKey) => {
    const base = applyColumnFilters(textFilteredRows, columnFilters, colKey);
    const uniq = new Set();
    for (const r of base) {
      uniq.add(cellFilterLabel(r[colKey]));
    }
    const sortedUniq = Array.from(uniq).sort((a, b) =>
      a.localeCompare(b, 'es', { numeric: true, sensitivity: 'base' }),
    );
    const allowed = columnFilters[colKey];
    const checked = {};
    for (const u of sortedUniq) {
      checked[u] = !allowed || allowed.length === 0 || allowed.includes(u);
    }
    setFilterMenuSearch('');
    setFilterMenuChecked(checked);
    setOpenFilterKey(colKey);
  };

  const applyColumnFilter = () => {
    if (!openFilterKey) return;
    const allCount = uniquesForOpenColumn.length;
    const selected = Object.entries(filterMenuChecked)
      .filter(([, on]) => on)
      .map(([label]) => label);
    if (selected.length === 0) {
      window.alert('Selecciona al menos un valor.');
      return;
    }
    setColumnFilters((prev) => {
      const next = { ...prev };
      if (allCount > 0 && selected.length >= allCount) {
        delete next[openFilterKey];
      } else {
        next[openFilterKey] = selected;
      }
      return next;
    });
    setOpenFilterKey(null);
    setFilterMenuPos(null);
  };

  const clearColumnFilter = (colKey, e) => {
    e?.stopPropagation?.();
    setColumnFilters((prev) => {
      const next = { ...prev };
      delete next[colKey];
      return next;
    });
    if (openFilterKey === colKey) {
      setOpenFilterKey(null);
      setFilterMenuPos(null);
    }
  };

  const clearAllColumnFilters = () => {
    setColumnFilters({});
    setOpenFilterKey(null);
    setFilterMenuPos(null);
  };

  const openFilterColumnDef = useMemo(
    () => (openFilterKey ? DETALLE_OTO_COLUMNS.find((c) => c.key === openFilterKey) : null),
    [openFilterKey],
  );

  const handleSort = (col) => {
    if (sortColumn === col) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortColumn(col); setSortDirection('asc'); }
  };

  const sortIcon = (col) => {
    if (sortColumn !== col) return <FaSort style={{ marginLeft: 6, opacity: 0.3 }} />;
    return sortDirection === 'asc'
      ? <FaSortUp style={{ marginLeft: 6 }} />
      : <FaSortDown style={{ marginLeft: 6 }} />;
  };

  const copiarDebugCesiones = async () => {
    const rut = searchTerm.trim();
    if (!rut) {
      window.alert('Escribe un RUT en el buscador (ej. 11714423-2) y vuelve a pulsar.');
      return;
    }
    if (!user) return;
    try {
      setDebugCesionesLoading(true);
      const result = await getDetalleOto(user, { page, page_size: pageSize, debug_rut: rut });
      const detalle = result?._meta_reporte?.cesiones?.debug_detalle_facturas;
      const metaCes = result?._meta_reporte?.cesiones;
      if (detalle == null) {
        window.alert(
          'La API no devolvió debug de cesiones. ¿Desplegaste la Lambda con soporte `debug_rut`? '
            + 'Mientras tanto, en consola expande: Resultado completo → _meta_reporte → cesiones.',
        );
        return;
      }
      const texto = JSON.stringify(
        { debug_rut: metaCes?.debug_rut ?? rut, filas: detalle },
        null,
        2,
      );
      // Misma info en consola (F12) por si el portapapeles falla
      console.log('[Gestión Campaña] debug cesiones', { debug_rut: rut, filas: detalle });
      await navigator.clipboard.writeText(texto);
      window.alert(
        `Listo: se copiaron ${Array.isArray(detalle) ? detalle.length : 0} filas de detalle al portapapeles. `
          + 'Pégalo en un mensaje o en VS Code para revisar folios y ramas de fecha.',
      );
    } catch (e) {
      console.error(e);
      window.alert(e?.message || 'No se pudo obtener el debug.');
    } finally {
      setDebugCesionesLoading(false);
    }
  };

  const filasElegiblesParaCarga = useMemo(() => {
    // Requisito del usuario: ruts que pasaron todos los filtros de detalle otorgamientos
    // => ESTADO FILTRO FINAL = OK y responsable válido.
    return sortedRows.filter((r) => {
      const ef = String(r?.['ESTADO FILTRO FINAL'] ?? '').trim().toUpperCase();
      const resp = String(r?.['RESPONSABLE MGC'] ?? '').trim();
      return ef === 'OK' && resp && resp.toUpperCase() !== 'NO APLICA';
    });
  }, [sortedRows]);

  const generarCsvGestionCartera = ({ limit }) => {
    const nombre = campanaNombre.trim();
    if (!nombre) {
      window.alert('Ingresa el nombre de la campaña.');
      return;
    }
    if (!fecIniCam || !fecFinCam) {
      window.alert('Selecciona fecha inicio y fecha fin.');
      return;
    }
    const start = parseInt(String(idCamStart || '').trim(), 10);
    if (!Number.isFinite(start) || start <= 0) {
      window.alert('Ingresa el ID_CAM inicial (número > 0).');
      return;
    }

    const base = Number.isFinite(limit) ? filasElegiblesParaCarga.slice(0, limit) : filasElegiblesParaCarga;
    if (base.length === 0) {
      window.alert('No hay filas elegibles en la vista actual (requiere ESTADO FILTRO FINAL = OK).');
      return;
    }

    const headers = [
      'ID_CAM',
      'RUT',
      'CLIENTE',
      'EJECUTIVO',
      'EQUIPO',
      'RESPONSABLE',
      'NOMBRE_CAM',
      'ORIGEN',
      'OFERTA',
      'FEC_INI_CAM',
      'FEC_FIN_CAM',
      'ESTADO',
      'PENETRACION',
    ].join(';');

    const lines = [headers];
    base.forEach((r, i) => {
      const row = [
        start + i,
        r?.RUT ?? r?.['RUT'] ?? '',
        r?.CLIENTE ?? r?.['CLIENTE'] ?? '',
        r?.EJECUTIVO ?? r?.['EJECUTIVO'] ?? '',
        r?.EQUIPO ?? r?.['EQUIPO'] ?? '',
        r?.['RESPONSABLE MGC'] ?? '',
        nombre,
        r?.['TAMAÑO FINAL'] ?? '',
        '', // OFERTA (pendiente)
        fecIniCam,
        fecFinCam,
        r?.ESTADO ?? '', // si algún día viene, si no queda vacío
        r?.['CATEGORÍA PENETRACIÓN'] ?? '',
      ].map(csvEscape);
      lines.push(row.join(';'));
    });

    const filename = `gestion_cartera_${nombre.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}${Number.isFinite(limit) ? `_test${limit}` : ''}.csv`;
    downloadCsv(filename, lines.join('\n'));
  };

  return (
    <div className="p-6">
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex items-baseline gap-4 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-800">Gestión Campaña</h1>
          <p className="text-gray-600">Detalle Otorgamientos (réplica Excel)</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/panel-marketing/fuentes-datos"
            className="btn-primary inline-flex items-center gap-2 text-sm py-2 px-4"
            style={{ textDecoration: 'none' }}
            title="Tablas MySQL y uso en el reporte"
          >
            <MdDataset className="w-4 h-4 shrink-0" />
            Fuentes del reporte
          </Link>
          <Link
            to="/detalle-otorgamientos"
            className="btn-primary inline-flex items-center gap-2 text-sm py-2 px-4 opacity-95"
            style={{ textDecoration: 'none', borderLeftColor: 'rgba(255,255,255,0.35)' }}
            title="Operaciones relacionadas con otorgamientos"
          >
            <MdReceipt className="w-4 h-4 shrink-0" />
            Detalle otorgamientos
          </Link>
          <Link
            to="/factoring"
            className="btn-primary inline-flex items-center gap-2 text-sm py-2 px-4 opacity-95"
            style={{ textDecoration: 'none', borderLeftColor: 'rgba(255,255,255,0.35)' }}
            title="Módulo factoring (cesiones en sf_factoring)"
          >
            <MdAccountBalance className="w-4 h-4 shrink-0" />
            Factoring
          </Link>
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-2 text-sm py-2 px-4"
            style={{ textDecoration: 'none', borderLeftColor: 'rgba(255,255,255,0.35)' }}
            onClick={() => setShowCargaCampana(true)}
            title="Genera CSV listo para importar en MGC.gestion_cartera"
          >
            Generar CSV (gestion_cartera)
          </button>
        </div>
      </div>

      {showCargaCampana && (
        <div className="seccion-datos mb-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex flex-col gap-3" style={{ minWidth: 320 }}>
              <div>
                <div className="text-sm font-semibold text-gray-800 mb-1">Nombre campaña</div>
                <input
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  value={campanaNombre}
                  onChange={(e) => setCampanaNombre(e.target.value)}
                  placeholder="Ej. Reactivar Dic. 24"
                />
              </div>
              <div className="flex gap-3 flex-wrap">
                <div>
                  <div className="text-sm font-semibold text-gray-800 mb-1">Fec. inicio</div>
                  <input
                    type="date"
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    value={fecIniCam}
                    onChange={(e) => setFecIniCam(e.target.value)}
                  />
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-800 mb-1">Fec. fin</div>
                  <input
                    type="date"
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    value={fecFinCam}
                    onChange={(e) => setFecFinCam(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-800 mb-1">ID_CAM inicial</div>
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  value={idCamStart}
                  onChange={(e) => setIdCamStart(e.target.value)}
                  placeholder="Ej. 1001"
                  min={1}
                />
                <div className="text-xs text-gray-500 mt-1">
                  Se usará ID_CAM = inicial + índice (para respetar tu carga incremental).
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <div className="text-sm text-gray-700">
                Filas elegibles en vista: <span className="font-semibold">{filasElegiblesParaCarga.length}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => generarCsvGestionCartera({ limit: 5 })}
                >
                  Generar CSV prueba (5)
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => generarCsvGestionCartera({ limit: null })}
                  disabled={filasElegiblesParaCarga.length === 0}
                >
                  Generar CSV normal (todo)
                </button>
                <button
                  type="button"
                  className="text-sm py-2 px-4 rounded-lg border border-gray-400 text-gray-800 bg-white font-medium hover:bg-gray-50"
                  onClick={() => setShowCargaCampana(false)}
                >
                  Cerrar
                </button>
              </div>
              <div className="text-xs text-gray-500 max-w-xl">
                El CSV queda listo para importar en `MGC.gestion_cartera`. ORIGEN = `TAMAÑO FINAL`. OFERTA queda vacía (pendiente).
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="seccion-datos mb-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="text-sm text-gray-600">
            Página <span className="font-semibold text-gray-800">{page}</span>
            {modoCargaCompleta && (
              <span className="ml-2 text-xs font-semibold text-amber-900 bg-amber-100 px-2 py-0.5 rounded">
                modo carga completa · page_size {pageSize.toLocaleString('es-CL')}
              </span>
            )}
            <span className="ml-2" style={{ color: 'var(--if-text-muted)' }}>
              ({sortedRows.length} en vista
              {activeColumnFilterCount > 0 && (
                <span className="text-blue-700 font-medium">
                  {' '}· {activeColumnFilterCount} filtro{activeColumnFilterCount === 1 ? '' : 's'} columna
                </span>
              )}
              )
            </span>
            {activeColumnFilterCount > 0 && (
              <button
                type="button"
                className="text-xs text-blue-700 underline hover:text-blue-900"
                onClick={clearAllColumnFilters}
              >
                Quitar filtros de columna
              </button>
            )}
          </div>

          <div className="relative" style={{ maxWidth: 520, width: '100%' }}>
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <IoSearchOutline className="h-5 w-5" style={{ color: '#9ca3af' }} />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por RUT, cliente, ejecutivo, equipo..."
              className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                style={{ color: '#9ca3af' }}
                title="Limpiar búsqueda"
              >
                <IoClose className="h-5 w-5" />
              </button>
            )}
          </div>

          <button
            type="button"
            className="text-xs py-2 px-3 rounded-lg border border-gray-400 text-gray-700 bg-white hover:bg-gray-50 shrink-0 whitespace-nowrap disabled:opacity-50"
            onClick={copiarDebugCesiones}
            disabled={debugCesionesLoading || !searchTerm.trim()}
            title="Pide a la Lambda el detalle por factura (debug_rut) y copia JSON al portapapeles"
          >
            {debugCesionesLoading ? 'Debug…' : 'Debug cesiones (copiar)'}
          </button>

          <div className="flex flex-wrap gap-2 items-center">
            <button className="btn-primary" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={loading || page === 1 || modoCargaCompleta}>
              Anterior
            </button>
            <button
              className="btn-primary"
              onClick={() => setPage(p => p + 1)}
              disabled={
                loading
                || modoCargaCompleta
                || (reportTotal != null && page * pageSize >= reportTotal)
              }
            >
              Siguiente
            </button>
            <button
              type="button"
              className="text-sm py-2 px-4 rounded-lg border border-amber-600 text-amber-900 bg-amber-50 font-medium hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={cargarTodosLosRegistros}
              disabled={
                loading
                || (modoCargaCompleta && reportTotal != null && rows.length >= reportTotal)
              }
              title="Una sola petición con page_size hasta 100.000 (prueba de rendimiento)"
            >
              Cargar todo
            </button>
            {modoCargaCompleta && (
              <button
                type="button"
                className="text-sm py-2 px-4 rounded-lg border border-gray-400 text-gray-800 bg-white font-medium hover:bg-gray-50"
                onClick={volverPaginacion}
                disabled={loading}
              >
                Volver a paginación ({DEFAULT_PAGE_SIZE})
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="seccion-datos">
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <AiOutlineLoading3Quarters className="animate-spin h-8 w-8" style={{ color: 'var(--if-primary)' }} />
          </div>
        ) : sortedRows.length === 0 ? (
          <div className="text-center py-10" style={{ color: 'var(--if-text-muted)' }}>
            {rows.length === 0
              ? 'No hay datos para mostrar'
              : 'Ninguna fila cumple la búsqueda o los filtros de columna. Prueba quitar filtros o ampliar la búsqueda.'}
          </div>
        ) : (
          <div className="tabla-container" style={{ overflowX: 'auto', maxWidth: '100%' }}>
            <table style={{ minWidth: 'max-content' }}>
              <thead>
                <tr>
                  {DETALLE_OTO_COLUMNS.map((col) => {
                    const alignEnd = ['money', 'ratioPercent', 'decimal', 'integer'].includes(col.format);
                    const hasColFilter = Array.isArray(columnFilters[col.key]) && columnFilters[col.key].length > 0;
                    return (
                      <th
                        key={col.key}
                        className="relative align-top"
                        style={{
                          textAlign: cellAlign(col.format),
                          verticalAlign: 'top',
                          ...(col.rutWide ? { minWidth: 148, whiteSpace: 'nowrap' } : {}),
                        }}
                      >
                        <div
                          className="flex items-start gap-1"
                          style={{
                            justifyContent: alignEnd ? 'flex-end' : 'flex-start',
                            flexWrap: 'nowrap',
                          }}
                        >
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 text-left hover:opacity-80 border-0 bg-transparent p-0 font-semibold cursor-pointer"
                            style={{ color: 'inherit', textAlign: alignEnd ? 'right' : 'left' }}
                            onClick={() => handleSort(col.key)}
                            title="Ordenar"
                          >
                            <span>{col.header}</span>
                            {sortIcon(col.key)}
                          </button>
                          <button
                            type="button"
                            className="shrink-0 mt-0.5 p-0.5 rounded border-0 bg-transparent cursor-pointer hover:bg-gray-200/80"
                            style={{
                              color: hasColFilter ? '#2563eb' : '#9ca3af',
                            }}
                            title="Filtrar por valores (como Excel)"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (openFilterKey === col.key) {
                                setOpenFilterKey(null);
                                setFilterMenuPos(null);
                              } else {
                                const r = e.currentTarget.getBoundingClientRect();
                                const panelW = 280;
                                setFilterMenuPos({
                                  top: r.bottom + 4,
                                  left: Math.max(8, Math.min(r.left, window.innerWidth - panelW - 8)),
                                });
                                openColumnFilterMenu(col.key);
                              }
                            }}
                          >
                            <FaFilter className="w-3.5 h-3.5" aria-hidden />
                          </button>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r, idx) => (
                  <tr key={`${r['RUT'] ?? 'x'}-${idx}`}>
                    {DETALLE_OTO_COLUMNS.map((col) => (
                      <td
                        key={col.key}
                        style={{
                          textAlign: cellAlign(col.format),
                          ...(col.rutWide
                            ? { minWidth: 148, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }
                            : {}),
                        }}
                      >
                        {renderCell(col, r)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && (reportTotal != null || rows.length > 0) && (
          <div
            className="mt-4 pt-4 border-t border-gray-200 text-sm text-gray-600 flex flex-wrap items-center gap-x-6 gap-y-2"
            style={{ borderColor: '#e5e7eb' }}
          >
            <span>
              <span className="font-semibold text-gray-800">Total en el reporte:</span>{' '}
              {reportTotal != null ? reportTotal.toLocaleString('es-CL') : '—'} filas
              {totalPages != null && totalPages > 0 && (
                <span className="text-gray-500"> ({totalPages.toLocaleString('es-CL')} páginas)</span>
              )}
            </span>
            <span>
              <span className="font-semibold text-gray-800">En esta página:</span>{' '}
              {rows.length.toLocaleString('es-CL')} filas cargadas
            </span>
            {!loading && rows.length > 0 && (searchTerm.trim() || activeColumnFilterCount > 0) && (
              <span style={{ color: 'var(--if-text-muted)' }}>
                Vista: {sortedRows.length.toLocaleString('es-CL')} de {rows.length.toLocaleString('es-CL')} filas cargadas
                {searchTerm.trim() && ' · búsqueda'}
                {activeColumnFilterCount > 0 && ` · ${activeColumnFilterCount} filtro(s) por columna`}
              </span>
            )}
          </div>
        )}
      </div>

      {openFilterColumnDef && filterMenuPos
        ? createPortal(
            <div
              ref={filterMenuRef}
              className="rounded-lg border border-gray-200 bg-white shadow-xl flex flex-col text-left"
              style={{
                position: 'fixed',
                top: filterMenuPos.top,
                left: filterMenuPos.left,
                zIndex: 10000,
                minWidth: 260,
                maxWidth: 320,
                maxHeight: 400,
                boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
              }}
              onMouseDown={(e) => e.stopPropagation()}
              role="dialog"
              aria-label={`Filtro ${openFilterColumnDef.header}`}
            >
              <div className="p-2 border-b border-gray-100 text-xs font-semibold text-gray-700">
                Filtrar: {openFilterColumnDef.header}
              </div>
              <div className="p-2 border-b border-gray-100 space-y-2">
                <input
                  type="search"
                  value={filterMenuSearch}
                  onChange={(e) => setFilterMenuSearch(e.target.value)}
                  placeholder="Buscar valor…"
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  autoFocus
                />
                <div className="flex flex-wrap gap-1">
                  <button
                    type="button"
                    className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200"
                    onClick={() => {
                      setFilterMenuChecked((prev) => {
                        const next = { ...prev };
                        for (const u of visibleFilterOptions) next[u] = true;
                        return next;
                      });
                    }}
                  >
                    Marcar visibles
                  </button>
                  <button
                    type="button"
                    className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200"
                    onClick={() => {
                      setFilterMenuChecked((prev) => {
                        const next = { ...prev };
                        for (const u of visibleFilterOptions) next[u] = false;
                        return next;
                      });
                    }}
                  >
                    Desmarcar visibles
                  </button>
                  {Array.isArray(columnFilters[openFilterKey]) && columnFilters[openFilterKey].length > 0 && (
                    <button
                      type="button"
                      className="text-xs px-2 py-1 rounded text-blue-700 hover:bg-blue-50"
                      onClick={() => clearColumnFilter(openFilterKey)}
                    >
                      Borrar filtro
                    </button>
                  )}
                </div>
              </div>
              <div className="overflow-y-auto px-2 py-1 text-sm flex-1" style={{ maxHeight: 220 }}>
                <label className="flex items-center gap-2 py-1 cursor-pointer border-b border-gray-100 mb-1 bg-white sticky top-0">
                  <input
                    type="checkbox"
                    checked={
                      visibleFilterOptions.length > 0
                      && visibleFilterOptions.every((u) => filterMenuChecked[u])
                    }
                    onChange={(e) => {
                      const on = e.target.checked;
                      setFilterMenuChecked((prev) => {
                        const next = { ...prev };
                        for (const u of visibleFilterOptions) next[u] = on;
                        return next;
                      });
                    }}
                  />
                  <span className="text-gray-600 font-medium">(Seleccionar todo lo visible)</span>
                </label>
                {visibleFilterOptions.length === 0 ? (
                  <div className="text-gray-500 text-xs py-2">Sin coincidencias</div>
                ) : (
                  visibleFilterOptions.map((u) => (
                    <label
                      key={u}
                      className="flex items-center gap-2 py-1 cursor-pointer hover:bg-gray-50 rounded px-1"
                    >
                      <input
                        type="checkbox"
                        checked={!!filterMenuChecked[u]}
                        onChange={(e) => {
                          setFilterMenuChecked((prev) => ({
                            ...prev,
                            [u]: e.target.checked,
                          }));
                        }}
                      />
                      <span className="truncate" title={u}>
                        {u}
                      </span>
                    </label>
                  ))
                )}
              </div>
              <div className="p-2 border-t border-gray-100 flex justify-end gap-2 shrink-0">
                <button
                  type="button"
                  className="text-xs px-3 py-1.5 rounded border border-gray-300 bg-white hover:bg-gray-50"
                  onClick={() => {
                    setOpenFilterKey(null);
                    setFilterMenuPos(null);
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="text-xs px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700"
                  onClick={applyColumnFilter}
                >
                  Aplicar
                </button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
};

export default PanelDetalleOto;

